export type MaterialProcurementEvidence = {
    procurementAction?: "REVIEW_REQUIRED" | "EXPEDITE_ORDER" | "TRANSFER_REQUIRED" | "PURCHASE" | "COVERED";
    purchaseQuantity?: number;
    reviewReasons?: string[];
    stockBreakdown?: {
        reportedOnHandQuantity: number;
        usableQuantity: number;
        excludedQuantity: number;
        negativeQuantity: number;
        reservedQuantity?: number;
        excludedWarehouses: Array<{
            warehouseCode: string;
            warehouseName: string;
            quantity: number;
        }>;
    };
    inboundEvents?: Array<{
        deliveryAt: string;
        quantity: number;
        orderNo: string;
        overdue: boolean;
        lateForPlan: boolean;
        approvalState: "UNKNOWN" | "UNAPPROVED" | "APPROVED";
        warehouseCode?: string | null;
        excludedWarehouse?: boolean;
    }>;
    dailyLedger?: Array<{
        date: string;
        demandQuantity: number;
        inboundQuantity: number;
        projectedQuantity: number;
        shortageQuantity: number;
    }>;
    demandSource?: "LINKED_WORK_ORDERS" | "RECIPE_FALLBACK";
    sourceWorkOrders?: Array<{
        workOrderNo: string;
        rootWorkOrderNo: string;
        outputCode: string;
        requiredAt: string;
        plannedQuantity: number;
        consumedQuantity: number;
        remainingQuantity: number;
    }>;
};
export function procurementActionLabel(material: MaterialProcurementEvidence): string {
    if (material.procurementAction === "REVIEW_REQUIRED")
        return "Kayıt kontrolü gerekli";
    if (material.procurementAction === "EXPEDITE_ORDER")
        return "Mevcut siparişi kontrol et / hızlandır";
    if (material.procurementAction === "TRANSFER_REQUIRED")
        return "Depo transferini kontrol et";
    if (material.procurementAction === "PURCHASE")
        return "Yeni satın alma";
    if (material.procurementAction === "COVERED")
        return "Hesaplanan ihtiyaç karşılanıyor";
    return "Güncel hesap gerekli";
}
export function canRequestMaterial(material: MaterialProcurementEvidence, currentAssessment: boolean) {
    return currentAssessment && material.procurementAction === "PURCHASE" &&
        material.demandSource === "LINKED_WORK_ORDERS" && (material.sourceWorkOrders?.length ?? 0) > 0 &&
        (material.reviewReasons?.length ?? 0) === 0 && (material.purchaseQuantity ?? 0) > 0;
}
export function procurementView(material: MaterialProcurementEvidence): "PURCHASE" | "REVIEW" | "ORDERS" | "COVERED" {
    if (!material.procurementAction || material.procurementAction === "REVIEW_REQUIRED" || material.reviewReasons?.length)
        return "REVIEW";
    if (material.procurementAction === "PURCHASE")
        return "PURCHASE";
    if (material.procurementAction === "EXPEDITE_ORDER" || material.procurementAction === "TRANSFER_REQUIRED")
        return "ORDERS";
    return "COVERED";
}
type ProcurementIssue = {
    code: string;
    message: string;
    materialCodes?: string[];
    sourceProducts?: string[];
};
export function groupProcurementReviews(materials: Array<MaterialProcurementEvidence & {
    materialCode: string;
}>, issues: ProcurementIssue[]) {
    const grouped = new Map<string, {
        message: string;
        materialCodes: Set<string>;
        sourceProducts: Set<string>;
    }>();
    for (const material of materials) {
        const sources = issues.filter((issue) => issue.materialCodes?.includes(material.materialCode)).flatMap((issue) => issue.sourceProducts ?? []);
        for (const reason of material.reviewReasons ?? []) {
            const group = grouped.get(reason) ?? { message: reason, materialCodes: new Set<string>(), sourceProducts: new Set<string>() };
            group.materialCodes.add(material.materialCode);
            sources.forEach((source) => group.sourceProducts.add(source));
            grouped.set(reason, group);
        }
    }
    for (const issue of issues.filter((item) => item.code !== "MATERIAL_REVIEW_REQUIRED")) {
        const group = grouped.get(issue.message) ?? { message: issue.message, materialCodes: new Set<string>(), sourceProducts: new Set<string>() };
        issue.materialCodes?.forEach((code) => group.materialCodes.add(code));
        issue.sourceProducts?.forEach((code) => group.sourceProducts.add(code));
        grouped.set(issue.message, group);
    }
    return [...grouped.values()].map((group) => ({ message: group.message, materialCodes: [...group.materialCodes], sourceProducts: [...group.sourceProducts] }))
        .sort((a, b) => b.sourceProducts.length - a.sourceProducts.length || b.materialCodes.length - a.materialCodes.length || a.message.localeCompare(b.message, "tr-TR"));
}
